import { H2 } from '@/common'
import cx from 'classnames'
import { useTranslation } from 'react-i18next'

type ProgressBarStyle = 25 | 50 | 100

const statusProgressBar: Record<ProgressBarStyle, string> = {
  100: 'bg-blue-500',
  50: 'bg-yellow-500',
  25: 'bg-red-500'
}

const statusProgressWrapper: Record<ProgressBarStyle, string> = {
  100: 'bg-blue-300',
  50: 'bg-yellow-300',
  25: 'bg-red-300'
}

function CountdownBar({
  value,
  maxValue,
  ongoing,
  completed
}: {
  value: number
  maxValue: number
  ongoing: boolean
  completed: boolean
}) {
  // Not started period
  if (!ongoing && !completed) {
    return <div className="h-2.5 w-full bg-gray-300" />
  }

  // Completed period
  if (completed) {
    return <div className={cx('h-2.5 w-full', 'bg-green-500')} />
  }

  // Ongoing period - calculate progress
  const percentage = Math.min(100, Math.max(0, (value / maxValue) * 100))

  let progressStyle: ProgressBarStyle = 100
  if (percentage <= 25) {
    progressStyle = 25
  } else if (percentage <= 50) {
    progressStyle = 50
  }

  return (
    <div className={cx('h-2.5 w-full flex justify-end', statusProgressWrapper[progressStyle])}>
      <div
        className={cx('h-2.5', statusProgressBar[progressStyle])}
        style={{ width: `${percentage}%` }}
      />
    </div>
  )
}

function CountdownLabel({
  pastDeadline,
  completed,
  ongoing,
  days
}: {
  pastDeadline: boolean
  completed: boolean
  ongoing: boolean
  days: number
}) {
  const { t } = useTranslation()

  return (
    <div className="flex gap-1 items-baseline">
      {ongoing && (
        <>
          <H2>{days}</H2>
          <span>{t('cbamReporting:cbamReportDetails.period.countdown.daysRemaining')}</span>
        </>
      )}
      {pastDeadline && !completed && (
        <span className="font-semibold pt-1.5">
          {t('cbamReporting:cbamReportDetails.period.countdown.pastDeadline')}
        </span>
      )}
      {completed && (
        <span className="font-semibold pt-1.5">
          {t('cbamReporting:cbamReportDetails.period.countdown.completedPeriod')}
        </span>
      )}
      {!ongoing && !completed && !pastDeadline && (
        <span className="font-semibold pt-1.5">
          {t('cbamReporting:cbamReportDetails.period.countdown.upcomingPeriod')}
        </span>
      )}
    </div>
  )
}

function CountdownSubmittedDate({
  submittedDate,
  periodEndDate,
  completed,
  deadlinePassed
}: {
  submittedDate: string
  periodEndDate: string
  completed: boolean
  deadlinePassed: boolean
}) {
  const { t } = useTranslation()

  return (
    <p className="text-xs flex items-center gap-2 font-light">
      {completed && deadlinePassed && (
        <>
          {t('cbamReporting:cbamReportDetails.period.countdown.completedOn')} {periodEndDate}
        </>
      )}

      {completed && !deadlinePassed && (
        <>
          {t('cbamReporting:cbamReportDetails.period.countdown.dueDate')} {submittedDate}
        </>
      )}

      {!completed && (
        <>
          {t('cbamReporting:cbamReportDetails.period.countdown.dueDate')} {submittedDate}
        </>
      )}
    </p>
  )
}

export default function QuarterCountdownV2({
  completed,
  ongoing,
  days,
  quarterDays,
  submittedDate,
  deadlinePassed,
  periodEndDate
}: {
  completed: boolean
  ongoing: boolean
  days: number
  quarterDays: number
  submittedDate: string
  deadlinePassed: boolean
  periodEndDate: string
}) {
  return (
    <div className="flex items-center gap-24 flex-direction-row">
      <div className="space-y-2 w-64">
        <CountdownLabel
          completed={completed}
          ongoing={ongoing}
          days={days}
          pastDeadline={deadlinePassed}
        />
        <CountdownBar value={days} maxValue={quarterDays} completed={completed} ongoing={ongoing} />
        <CountdownSubmittedDate
          submittedDate={submittedDate}
          completed={completed}
          deadlinePassed={deadlinePassed}
          periodEndDate={periodEndDate}
        />
      </div>
      <div className="flex items-center"></div>
    </div>
  )
}
